import { saveAs } from 'file-saver';
import go from 'gojs';
import html2canvas from 'html2canvas';
import React from 'react';
import ReactDOM from 'react-dom';
import { writeErr } from '../../../components/common/logger';
import { isFirefox } from '../../../components/common/utils';
import ChartDiagramExportTemplate from '../../../components/relationChart/exportTemplates/chartDiagramExportTemplate';
import { apiGet, apiPost, getHostApi, getHostUrl, hasPermission } from '../../apiWrapper';
import { getType } from '../../browserDetection';
import { padDate, updateObject } from '../../storeFunctions';
import { GRAPH_MODE, VISIBILITY_TYPE, _name } from '../common/constants';
import { _eventLoggerSend, _handleError } from '../common/functions';
import { _getRootElement } from '../diagram/actions';
import { diagram } from '../diagram/initial';
import { chartExportTypes } from './initial';
import { _hasAnyDeletedPart } from '../diagram/visiblityActions';

import OpenSansFont from '../../../fonts/OpenSans.woff';
import OpenSansItalicFont from '../../../fonts/OpenSansItalic.woff';
import OpenSansSemiBoldFont from '../../../fonts/OpenSansSemiBold.woff';
import OpenSansSemiBoldItalicFont from '../../../fonts/OpenSansSemiBoldItalic.woff';
import AvesomeFont from '../../../fonts/fontawesome-webfont.woff';
import IcoMoonFont from '../../../fonts/icomoon-dv.woff';

// KEYS
const actions = {
    EXPORT_UPDATE: _name + '_EXPORT_UPDATE',
};

// MAP
const actionMap = {
    [`${actions.EXPORT_UPDATE}`]: (state, data) => updateObject(state, { exports: updateObject(state.exports, data) }),
}

// ACTIONS

const browser = getType();

const chartActions = {
    // Exports
    exportToXlsx: (xlsxType) => async (dispatch, getState) => {
        const state = getState();
        if (state.charts.exports.exporting !== null)
            return;

        dispatch({ type: actions.EXPORT_UPDATE, data: { exporting: xlsxType, exportUsed: state.charts.exports.exportUsed.concat([xlsxType]) } })
        const exportModel = _getExportModel(state.charts);
        _eventLoggerSend("Export", xlsxType === chartExportTypes.xlsxFull ? "Excel Full" : xlsxType === chartExportTypes.xlsxNi ? "Excel NI" : "Excel SL");

        apiPost(
            `${getHostApi()}/api/charts/export/xlsx`, undefined, { type: xlsxType, data: exportModel },
            (s) => {
                if (!s || !s.data || s.error)
                    dispatch({ type: actions.EXPORT_UPDATE, error: { exporting: null, exception: s.error } })
            },
            (e) => dispatch({ type: actions.EXPORT_UPDATE, error: { exporting: null, exception: e } })
        )
        dispatch({ type: actions.EXPORT_UPDATE, data: { exporting: null } })
    },
    exportToPdf: (print = false) => async (dispatch, getState) => {
        const state = getState();
        if (state.charts.exports.exporting !== null)
            return;

        dispatch({ type: actions.EXPORT_UPDATE, data: { exporting: print ? chartExportTypes.printPdf : chartExportTypes.pdf, exportUsed: state.charts.exports.exportUsed.concat(print ? [chartExportTypes.printPdf] : [chartExportTypes.pdf]) } });
        const exportModel = _getExportModel(state.charts);
        _eventLoggerSend("Export", "PDF");

        if (print) {
            apiPost(`${getHostApi()}/api/charts/export/pdfprint`, undefined, { type: chartExportTypes.pdf, data: exportModel },
                s => _downloadExport(state.userContext, s.fileId, print),
                f => _handleError('/api/charts/export/pdf error', f, getState),
                _ => dispatch({ type: actions.EXPORT_UPDATE, data: { exporting: null } })
            );
        } else {
            apiPost(
                `${getHostApi()}/api/charts/export/pdf-export`, undefined, { type: chartExportTypes.pdf, data: exportModel },
                (s) => {
                    if (!s || !s.data || s.error)
                        dispatch({ type: actions.EXPORT_UPDATE, error: { exporting: null, exception: s.error } })
                },
                (e) => dispatch({ type: actions.EXPORT_UPDATE, error: { exporting: null, exception: e } })
            )
            dispatch({ type: actions.EXPORT_UPDATE, data: { exporting: null } })
        }
    },
    exportToPng: (translations) => async (dispatch, getState) => {
        const state = getState();
        if (state.charts.exports.exporting !== null)
            return;
        dispatch({ type: actions.EXPORT_UPDATE, data: { exporting: chartExportTypes.png, exportUsed: state.charts.exports.exportUsed.concat([chartExportTypes.png]) } });
        _eventLoggerSend("Export", "PNG");

        const isIE = navigator.userAgent.match(/Trident\/7\./) !== null;
        const exportContext = document.querySelector('#export-context');
        const agentLang = state.userContext.authentication.profile.agentLanguage;
        const template = _getDiagramImageExportTemplate(translations, agentLang, true, isIE, getState().userContext.authentication.profile.username);
        ReactDOM.render(template, exportContext);

        const name = _getReportName(translations["shortName"], ".png");
        html2canvas(exportContext, { useCORS: true, x: 0, y: 0, width: template.props.wrapperWidth }).then(
            c => {
                if (isIE || browser === 'Edge') {
                    apiPost(`${getHostApi()}/api/charts/export/image`, undefined, { data: c.toDataURL(), name: name },
                        s => _downloadExport(state.userContext, s.fileId),
                        e => _handleError('/api/charts/export/image error', e, getState),
                        _ => {
                            dispatch({ type: actions.EXPORT_UPDATE, data: { exporting: null } });
                        }
                    );
                }
                else {
                    c.toBlob(function (blob) {
                        saveAs(blob, name);
                    });
                    dispatch({ type: actions.EXPORT_UPDATE, data: { exporting: null } });
                }
            },
            e => {
                _handleError('html2canvas error', e, getState);
                dispatch({ type: actions.EXPORT_UPDATE, data: { exporting: null } });
            }
        );

        ReactDOM.unmountComponentAtNode(exportContext);
    },
    exportToPrintDiagram: (translations) => async (dispatch, getState) => {
        const state = getState();
        if (state.charts.exports.exporting !== null)
            return;
        dispatch({ type: actions.EXPORT_UPDATE, data: { exporting: chartExportTypes.printDiagram, exportUsed: state.charts.exports.exportUsed.concat([chartExportTypes.printDiagram]) } });
        _eventLoggerSend("Export", "PrintDiagram");

        const agentLang = state.userContext.authentication.profile.agentLanguage;
        const template = _getDiagramImageExportTemplate(translations, agentLang, false, getState().userContext.authentication.profile.username);

        const printWin = window.open('', 'PRINT', '');
        const wrapper = printWin.document.createElement('div');
        ReactDOM.render(template, wrapper);
        if (printWin.document.body.childNodes.length > 0)
            ReactDOM.unmountComponentAtNode(printWin.document.body.childNodes[0]);
        printWin.document.body.appendChild(wrapper);
        printWin.onbeforeunload = () => ReactDOM.unmountComponentAtNode(wrapper);
        dispatch({ type: actions.EXPORT_UPDATE, data: { exporting: null } });
    },
    exportToSvg: (translations) => async (dispatch, getState) => {
        const state = getState();
        if (state.charts.exports.exporting !== null)
            return;
        dispatch({ type: actions.EXPORT_UPDATE, data: { exporting: chartExportTypes.svg, exportUsed: state.charts.exports.exportUsed.concat([chartExportTypes.svg]) } });
        const dgrm = diagram.nodeDiagram;
        _eventLoggerSend("Export", "SVG");
        _createSvgWithFont(dgrm, translations, getState).then(svgData => {
            var data = new XMLSerializer().serializeToString(svgData);
            data = data.replace(/\/static\/media/g, window.location.origin + '/static/media');
            const name = _getReportName(translations["shortName"], '.svg');
            var blob = new Blob([data], { type: "image/svg+xml" });
            saveAs(blob, name);
            dispatch({ type: actions.EXPORT_UPDATE, data: { exporting: null } });
        });
    },
    exportToCsv: (csvType) => async (dispatch, getState) => {
        const state = getState();
        if (state.charts.exports.exporting !== null)
            return;

        dispatch({ type: actions.EXPORT_UPDATE, data: { exporting: null, exportUsed: state.charts.exports.exportUsed.concat([csvType]) } })
        const exportModel = _getExportModel(state.charts);
        _eventLoggerSend("Export", csvType === chartExportTypes.csvSl ? "CSV SL" : "CSV ???");

        apiPost(
            `${getHostApi()}/api/charts/export/csv`, undefined,
            { type: csvType, data: exportModel },
            (s) => {
                if (!s || !s.data || s.error)
                    dispatch({ type: actions.EXPORT_UPDATE, error: { exporting: null, exception: s.error } })
            },
            (e) => dispatch({ type: actions.EXPORT_UPDATE, error: { exception: e } })
        )
    },
}

// FUNCTIONS
function _resetExportButtons(dispatch) {
    dispatch({ type: actions.EXPORT_UPDATE, data: { exportUsed: [] } });
}

function _getExportModel(state, excludeDeleted = true) {
    const lockedNodeIds = [];
    const expandedNodeIds = [];

    diagram._nodes.forEach(n => {
        if (n.IsLocked)
            lockedNodeIds.push(n.EntityID);
        if (n.IsExpanded)
            expandedNodeIds.push(n.EntityID);
    });

    const deletedNodeIds = excludeDeleted ? _getDeletedNodesIds() : [];
    const deletedRelations = excludeDeleted ? _getDeletedRelations() : [];

    var filterConfiguration = {};
    if (diagram._mode === GRAPH_MODE.path) {
        expandedNodeIds.push(diagram._rootIdList[1]);
        filterConfiguration = {
            historical: diagram._initParams.hist === 'true',
            histFromYear: diagram._initParams.from,
            histToYear: diagram._initParams.to,
            level: parseInt(diagram._initParams.level),
            ownershipInterest: state.leftPanel.shareChecked ? state.leftPanel.shareValue : 0,
            relationTypeCodes: diagram._initParams.rtFull,
            warningTypeCodes: state.leftPanel.wt,
            relationDirection: state.leftPanel.linkDirection
        }
    }
    else
        filterConfiguration = {
            historical: state.leftPanel.history,
            histFromYear: state.leftPanel.dateFrom,
            histToYear: state.leftPanel.dateTo,
            level: state.leftPanel.level,
            ownershipInterest: state.leftPanel.shareChecked ? state.leftPanel.shareValue : 0,
            relationTypeCodes: state.leftPanel.rt,
            warningTypeCodes: state.leftPanel.wt,
            relationDirection: state.leftPanel.linkDirection
        };

    return {
        entityIdsList: diagram._rootIdList,
        isPath: diagram._mode === GRAPH_MODE.path,
        lockedNodeIds: lockedNodeIds,
        expandedNodeIds: expandedNodeIds,
        deletedNodeIds: deletedNodeIds,
        deletedRelations: deletedRelations,
        profiVersion: state.topPanel.isProfi,
        filterConfiguration
    };
}

function _getDeletedNodesIds() {
    const hiddenNodesIds = [];
    const it = diagram.nodeDiagram.nodes.iterator;
    while (it.next()) {
        const node = it.value;
        if (node.data.Visibility === VISIBILITY_TYPE.deleted) {
            hiddenNodesIds.push(node.data.EntityID);
        }
    }
    return hiddenNodesIds;
}

function _getDeletedRelations() {
    const deletedLinksData = [];
    const it = diagram.nodeDiagram.links.iterator;
    while (it.next()) {
        const link = it.value;
        if (link.data.Visibility === VISIBILITY_TYPE.deleted) {
            deletedLinksData.push(({
                ParentID: link.data.ParentID,
                ChildID: link.data.ChildID,
                Polarity: link.data.Polarity,
                State: link.data.State,
                RootGroupId: link.data.RootGroupId,
                Guaranteed: link.data.Guaranteed,
                RootRelationShortening: link.data.RootRelationShortening,
                SharePct: link.data.SharePct
            }));
        }
    }
    return deletedLinksData;
}

function _downloadExport(userContext, fileId, print = false) {
    try {
        if (print && !isFirefox()) {
            apiGet(`${getHostApi()}/api/charts/export/download/${fileId}`, null,
                (response) => {
                    const { data, contentType } = response.data;
                    const uint8Array = new Uint8Array(atob(data).split('').map(char => char.charCodeAt(0)));
                    const blob = new Blob([uint8Array], { type: contentType });
                    const webUrl = window.URL.createObjectURL(blob);

                    let iframe = document.createElement('iframe'); //load content in an iframe to print later
                    document.body.appendChild(iframe);
                    iframe.style.display = 'none';
                    iframe.src = webUrl;
                    iframe.onload = function () {
                        setTimeout(function () {
                            iframe.focus();
                            iframe.contentWindow.print();
                        }, 1);
                    }
                },
                (e) => {
                    console.error(e)
                }
            );
        } else {
            if (window !== undefined)
                window.open(`${getHostUrl()}/files/charts/export/${fileId}`, '_blank');
        }
    } catch (e) {
        writeErr("PrintPdf error", e);
    }
}

function _getDiagramImageExportTemplate(translations, agentLang, showDisclaimer, isIE = false, username = "") {
    const dgrm = diagram.nodeDiagram;
    const scale = dgrm.scale;
    const width = Math.ceil((dgrm.documentBounds.width + 15) * scale);
    const height = Math.ceil((dgrm.documentBounds.height + 15) * scale);
    const disclaimerWidth = 1150;
    const wrapperWidth = showDisclaimer && width + 25 < disclaimerWidth ? disclaimerWidth : width + 25;
    const wrapperHeight = height + 180;

    const diagramImage = dgrm.makeImage({
        background: "transparent",
        showTemporary: true,
        position: new go.Point(dgrm.documentBounds.x - 5, dgrm.documentBounds.y - 5),
        size: new go.Size(width, height),
        maxSize: new go.Size(width, height),
        scale: scale
    });

    return (
        <ChartDiagramExportTemplate
            forceImg={isIE}
            agentLanguage={agentLang}
            translations={translations}
            diagramImage={diagramImage}
            width={width}
            height={height}
            showDisclaimer={showDisclaimer}
            disclaimerWidth={disclaimerWidth}
            wrapperWidth={wrapperWidth}
            wrapperHeight={wrapperHeight}
            username={username}
            hasDeletedParts={_hasAnyDeletedPart()}
        />
    );
}

const blobToBase64 = blob => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    return new Promise(resolve => {
        reader.onloadend = () => {
            resolve(reader.result);
        };
    });
};

function fetchFile(file) {
    return new Promise(resolve => {
        fetch(file)
            .then(response => response.blob())
            .then(function (myBlob) {
                blobToBase64(myBlob).then(res => {
                    resolve(res);
                });
            });
    });
}

function _createSvgWithFont(dgrm, translations, getState) {
    const svgNs = 'http://www.w3.org/2000/svg';
    const svg = dgrm.makeSvg({
        showTemporary: true,
        scale: 1,
        position: new go.Point(dgrm.documentBounds.x, dgrm.documentBounds.y - 40),
        size: new go.Size(dgrm.documentBounds.width, dgrm.documentBounds.height + 40)
    });
    const defs = document.createElement('defs');
    const svgStyles = document.createElement('style');

    return new Promise(resolve => {
        fetchFile(IcoMoonFont).then(icoMoonFont => {
            fetchFile(AvesomeFont).then(avesomeFont => {
                fetchFile(OpenSansFont).then(openSansFont => {
                    fetchFile(OpenSansSemiBoldFont).then(openSansSemiBoldFont => {
                        fetchFile(OpenSansItalicFont).then(openSansItalicFont => {
                            fetchFile(OpenSansSemiBoldItalicFont).then(openSansSemiBoldItalicFont => {
                                const svgStylesText = [
                                    '@font-face {font-family: "icomoon-dv";src: url("' + icoMoonFont + '");}',
                                    '@font-face {font-family: "FontAwesome";src: url("' + avesomeFont + '");}',
                                    '@font-face {font-family: "Open Sans";font-style: normal;font-weight: 400;src: url("' + openSansFont + '");}',
                                    '@font-face {font-family: "Open Sans";font-style: normal;font-weight: 600;src: url("' + openSansSemiBoldFont + '");}',
                                    '@font-face {font-family: "Open Sans";font-style: italic;font-weight: 400;src: url("' + openSansItalicFont + '");}',
                                    '@font-face {font-family: "Open Sans";font-style: italic;font-weight: 600;src: url("' + openSansSemiBoldItalicFont + '");}'
                                ];
                                svgStyles.innerText = svgStylesText.join('');
                                defs.append(svgStyles);
                                svg.prepend(defs);

                                const tempParentElement = document.createElement('div');
                                svg.appendChild(tempParentElement);

                                const firstLine = _createSvgKeyValueLine(svgNs, 5, 20, translations["reportDate"] + ': ', padDate(new Date().getDate()) + '.' + padDate(new Date().getMonth() + 1) + '.' + new Date().getFullYear());
                                tempParentElement.insertAdjacentElement('afterend', firstLine);

                                if (!hasPermission(null, "UsernameInvisibility")) {
                                    const secondLine = _createSvgKeyValueLine(svgNs, 5, 40, translations["username"] + ': ', getState().userContext.authentication.profile.username);
                                    tempParentElement.insertAdjacentElement('afterend', secondLine);
                                }

                                if (_hasAnyDeletedPart()) {
                                    const thirdLine = _createSvgKeyValueLine(svgNs, 5, 60, translations["removedSubjects"] + ': ', translations["yes"]);
                                    tempParentElement.insertAdjacentElement('afterend', thirdLine);
                                }

                                tempParentElement.parentNode.removeChild(tempParentElement);
                                resolve(svg);
                            });
                        });
                    });
                });
            });
        });
    });
}

function _createSvgKeyValueLine(svgNs, x, y, keyContent, valueContent) {
    // text
    const text = document.createElementNS(svgNs, 'text');
    text.setAttribute('x', x);
    text.setAttribute('y', y);
    // div
    const tempDiv = document.createElement('div');
    text.appendChild(tempDiv);
    // key
    const keyLabel = document.createElementNS(svgNs, 'tspan');
    keyLabel.setAttribute('font-weight', 'bold');
    keyLabel.textContent = keyContent;
    // value
    const valueLabel = document.createElementNS(svgNs, 'tspan');
    valueLabel.textContent = valueContent;
    // insert
    tempDiv.insertAdjacentElement('afterend', valueLabel);
    tempDiv.insertAdjacentElement('afterend', keyLabel);
    // remove temp div
    tempDiv.parentNode.removeChild(tempDiv);
    return text;
}

function _getRootNodeId(dgrm) {
    var el = _getRootElement(dgrm);
    if (dgrm._mode !== GRAPH_MODE.merge) {
        if (el.IC !== null)
            return el.IC;
        if (el.Name !== null)
            return el.Name.replace('\\', '').replace('/', '').replace(':', '').replace('?', '').replace('*', '').replace('"', '')
                .replace('<', '').replace('>', '').replace('|', '');
    } else
        return "merge";
    return "";
}

function _getReportName(shortName, extension) {
    const now = new Date();
    return shortName + '_' + _getRootNodeId(diagram.nodeDiagram) + '_' + padDate(now.getDate()) + padDate(now.getMonth() + 1) + now.getFullYear() + extension;
}

export {
    _getExportModel, _resetExportButtons,
    actionMap as exportActionMap, actions as exportActions, chartActions as exportChartActions
};
